import type { Collection, Poem } from './poem.ts'

/**
 * Poem vocabulary and pure formatting helpers — browser-safe.
 *
 * Re-exported from `./config.ts`, which is the single Zod-free entry point the
 * client is allowed to import values from. The rule that makes that entry point
 * worth having applies here verbatim: **every import in this module must be
 * `import type`**. A value import would pull Zod into the client bundle, and
 * Zod's `$ZodObjectJIT` probes `new Function("")`, which `script-src 'self'`
 * blocks. The schemas that validate these values live in `./poem.ts`.
 *
 * Every function here is pure, total and O(1) over a bounded input.
 */

/** Theme labels are user-visible pt-BR strings, so they are the values themselves. */
export const POEM_THEMES = Object.freeze([
  'Pássaros',
  'Natureza',
  'Casa',
  'Memória',
] as const)

export type PoemTheme = (typeof POEM_THEMES)[number]

/** The nine icons an index card may carry. Geometry lives in shared-ui-web. */
export const POEM_ICON_NAMES = Object.freeze([
  'bird',
  'chuva',
  'feather',
  'janela',
  'leaf',
  'moon',
  'smile',
  'sun',
  'wind',
] as const)

export type PoemIconName = (typeof POEM_ICON_NAMES)[number]

/**
 * The twenty-three decorative illustrations — one per poem, named for its subject.
 *
 * There used to be seventeen, shared: three poems drew `galho`, three drew
 * `jardim`, two drew `chuva`, two drew `orvalho`. Sharing was what made the
 * density and tempo flags necessary in the first place, and it meant the two
 * poems on a shared scene each got a picture that was true of neither — `chuva`
 * could not be both the driving shower the animals run through in `Dia de chuva`
 * and the still, empty cold of `O Dia Está Gelado`.
 *
 * One drawing per poem retires that whole apparatus. There is nothing left to
 * parameterise, because each illustration answers to exactly one text and can
 * simply contain what that text describes.
 *
 * WHAT THESE NAMES DENOTE CHANGED IN PHASE 0016; WHAT THEY MEAN DID NOT. Each was
 * a bespoke hand-drawn scene and is now a small emblem composed of two or three
 * vendored Twemoji icons, chosen in `features/scenes/scene-art.ts` and justified
 * there against a line of the poem. The identifier still names the poem's
 * subject, the enum is still a bijection with the collection, and nothing
 * downstream of this file had to move — which is the point of naming a scene for
 * what it shows rather than for how it was made.
 *
 * `ronronar` and `festa` exist in the design prototype but are unreachable from
 * both of its scene maps, so they were never migrated — see the migration
 * charter, conflict C4.
 */
export const SCENE_NAMES = Object.freeze([
  'andorinhas',
  'arvores',
  'chuva',
  'cigarras',
  'escada',
  'gelado',
  'janela',
  'jardim',
  'lua',
  'mima',
  'natureza',
  'noite',
  'palhaco',
  'pardais',
  'pardaizinhos',
  'passarinhos',
  'pomar',
  'pombas',
  'quintal',
  'romeu',
  'sereno',
  'sitio',
  'sol',
] as const)

export type SceneName = (typeof SCENE_NAMES)[number]

/**
 * The two published collections.
 *
 * The design carried a `total` field alongside these; it existed only to drive an
 * empty-collection placeholder that can never render now that both collections are
 * fully populated (charter § 5.3). Counts are derived from the poems themselves.
 */
export const COLLECTIONS: readonly Collection[] = Object.freeze([
  { id: 1, label: 'Primeira coleção', badge: '1ª coleção' },
  { id: 2, label: 'Segunda coleção', badge: '2ª coleção' },
])

const COLLECTION_INDEX: ReadonlyMap<number, Collection> = new Map(
  COLLECTIONS.map((collection) => [collection.id, collection]),
)

/** O(1). Total over the `1 | 2` union the schema admits. */
export function findCollection(id: Collection['id']): Collection | undefined {
  return COLLECTION_INDEX.get(id)
}

// --- Publication dates -------------------------------------------------------

/**
 * How much of a publication date is actually known.
 *
 * Six poems carry month-and-year only and two carry no date at all. Padding an
 * unknown day to `-01` would fabricate a fact and then publish it in JSON-LD, so
 * the precision is derived from the stored value and never stored alongside it.
 */
export type DatePrecision = 'day' | 'month' | 'year' | 'unknown'

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const MONTH_PATTERN = /^\d{4}-\d{2}$/
const YEAR_PATTERN = /^\d{4}$/

/** O(1), total: any string that is not a reduced ISO date reads as `unknown`. */
export function datePrecision(value?: string): DatePrecision {
  if (value === undefined) {
    return 'unknown'
  }
  if (DAY_PATTERN.test(value)) {
    return 'day'
  }
  if (MONTH_PATTERN.test(value)) {
    return 'month'
  }
  if (YEAR_PATTERN.test(value)) {
    return 'year'
  }
  return 'unknown'
}

/**
 * Fixed pt-BR month names.
 *
 * Deliberately not `Intl.DateTimeFormat`: a reduced-precision date has no `Date`
 * to format, and `Intl` output varies by ICU build, which would make prerendered
 * HTML non-reproducible across machines.
 */
const MONTH_NAMES = Object.freeze([
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const)

function monthName(monthNumber: number): string | undefined {
  return MONTH_NAMES[monthNumber - 1]
}

/**
 * Human-readable pt-BR rendering, or `undefined` when the date is unknown.
 *
 * `undefined` is the signal to omit the `<time>` element entirely — an empty
 * `datetime` attribute is invalid HTML and a misleading SEO signal.
 */
export function formatPublishedDate(value?: string): string | undefined {
  if (value === undefined) {
    return undefined
  }

  const precision = datePrecision(value)
  if (precision === 'unknown') {
    return undefined
  }

  const year = value.slice(0, 4)
  if (precision === 'year') {
    return year
  }

  const month = monthName(Number(value.slice(5, 7)))
  if (month === undefined) {
    return undefined
  }

  return precision === 'month'
    ? `${month} de ${year}`
    : `${String(Number(value.slice(8, 10)))} de ${month} de ${year}`
}

// --- Descriptions ------------------------------------------------------------

/** `pageMetadataSchema.description` caps at 300, so the derived value always fits. */
const DESCRIPTION_MAX_LENGTH = 160
const DERIVED_LINE_COUNT = 2
const LINE_SEPARATOR = ' / '

function truncateOnWordBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value
  }

  const clipped = value.slice(0, maxLength)
  const lastSpace = clipped.lastIndexOf(' ')

  return `${lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped}…`
}

/**
 * The description a crawler and an index card both see.
 *
 * No summaries were authored for the real collection, and inventing twenty-three
 * blurbs in the poet's voice is not the migrator's call — so the fallback is the
 * poem's own opening, which is honest by construction.
 */
export function poemDescription(poem: Poem): string {
  if (poem.summary !== undefined) {
    return poem.summary
  }

  const opening = (poem.stanzas[0] ?? []).slice(0, DERIVED_LINE_COUNT).join(LINE_SEPARATOR)

  return truncateOnWordBoundary(opening, DESCRIPTION_MAX_LENGTH)
}
